import { Modal } from "../../components/Modal";
import styles from "./RoleGuideModal.module.css";

/**
 * Plain-English guide to the identity model behind /admin/users, rendered as a
 * modal. Pure presentation — every fact mirrors server/auth.js
 * (ROLE_CATALOGUE) and the permission gates on the /api/jobs route family, so
 * an admin can decide what to tick without reading code.
 *
 * @param {{ onClose: () => void }} props
 */
export function RoleGuideModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Roles and access" subtitle="Account types, job access and contractor details" onClose={onClose}>
      <div className={styles.body}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>How access adds up</h3>
          <p>
            Everyone has one <strong>account type</strong>. Staff and contractor accounts are then given a level of{" "}
            <strong>job access</strong> on top of it. What someone can do is everything their
            account type allows <em>plus</em> everything their job access allows — the two add up,
            they never cancel out.
          </p>
          <pre className={styles.formula}>what they can do = account type + job access</pre>
          <p>
            The rules are enforced on the server, not in the screens you see. Even if someone reaches
            a page they should not, the server refuses to hand over the data. New access takes effect
            the moment you save — see &ldquo;Worth knowing&rdquo; at the bottom for what happens to
            anyone who is signed in at the time.
          </p>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Account types (the starting point)</h3>
          <dl className={styles.definitions}>
            <dt>Admin</dt>
            <dd>
              Can do everything, with no exceptions: the whole jobs system (viewing, creating,
              editing, attachments, quotes and invoices) and full control of user accounts. The main
              admin account is protected — other admins cannot suspend it or take away its access.
            </dd>
            <dt>Manager</dt>
            <dd>
              Runs the day-to-day: the whole jobs system, plus looking after people — adding and
              suspending accounts, changing what someone can do, resetting passwords and inviting
              clients to the portal.
            </dd>
            <dt>Staff member</dt>
            <dd>
              A normal staff account. On its own it lets someone sign in and request a password reset — it
              gives <strong>no</strong> access to jobs at all. The job access setting (below) is
              what puts them on the board, which is why new staff accounts start on full access.
            </dd>
            <dt>Contractor</dt>
            <dd>
              An external trade or service provider with their own sign-in account. Contractors
              start with <strong>No job access</strong>. An administrator can grant View only or
              Full access later; both apply to <strong>all jobs</strong>, not just assigned jobs.
              Services and membership do not grant access. Recorded as the <code>contractor</code> account type.
            </dd>
            <dt>Client portal</dt>
            <dd>
              A customer you have invited to the portal. They get none of the staff access, and
              there is no job access setting to make for them. What they see — approving their own
              quotes and invoices at /portal — is decided by which client they are linked to.
            </dd>
          </dl>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Job access (staff and contractors)</h3>
          <dl className={styles.definitions}>
            <dt>Full access</dt>
            <dd>
              Everything &ldquo;view only&rdquo; allows, and on top of that: creating and editing
              jobs, moving cards around the board, adding and removing attachments, writing quotes
              and raising invoices. Recorded on the account as the{" "}
              <code>jobs</code> role.
            </dd>
            <dt>View only</dt>
            <dd>
              Look, but don&rsquo;t touch. They can browse the board and open any job, see the
              totals, read its quotes and invoice, and <strong>download attachments</strong> — but
              they cannot create, edit, move, upload or delete anything. Good for someone who needs
              to keep an eye on the work without being able to change it. Recorded as the{" "}
              <code>jobs-viewer</code> role.
            </dd>
            <dt>No job access</dt>
            <dd>
              They can sign in, view their account and request a password reset. Job data stays hidden,
              and they are excluded from job-member selection and job notifications. This is the default for contractors.
            </dd>
          </dl>
          <p>
            This choice applies to staff and contractor accounts. Admins and managers always have full access
            to jobs, so the Users screen does not offer them the choice at all.
          </p>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Who can do what, at a glance</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Access</th>
                <th scope="col">What it allows</th>
                <th scope="col">Who has it</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>users.manage</code></td>
                <td>Add and suspend accounts, change what people can do, reset passwords, invite clients to the portal</td>
                <td>admin, manager</td>
              </tr>
              <tr>
                <td><code>users.read</code></td>
                <td>See and search the list of people</td>
                <td>admin, manager</td>
              </tr>
              <tr>
                <td><code>jobs.read</code></td>
                <td>
                  The jobs board and every job, value totals, the next job number, a job&rsquo;s
                  quotes and invoice, and <strong>downloading attachments</strong>
                </td>
                <td>admin, manager, jobs, jobs-viewer</td>
              </tr>
              <tr>
                <td><code>jobs.write</code></td>
                <td>
                  Create and edit jobs, move cards around the board, <strong>add and remove
                  attachments</strong>, write quotes, raise invoices
                </td>
                <td>admin, manager, jobs</td>
              </tr>
              <tr>
                <td><code>profile.read</code> / <code>profile.write</code></td>
                <td>Account self-service permissions; contact an administrator to edit details</td>
                <td>staff, contractors, managers and admins</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Contractor details and services</h3>
          <ul className={styles.list}>
            <li>Email, first name, last name, one ABN or ACN, and at least one service are required.</li>
            <li>Choose Personal name or Business name as the display-name source. Business name is required when selected. A manual override takes precedence; clearing it restores the chosen source.</li>
            <li>ABN and ACN checks validate format and checksum only. They do not verify registration status.</li>
            <li>Search the grouped services catalogue or add a custom service for all administrators to reuse. Tags describe work performed; they do not certify qualifications, licences or insurance.</li>
            <li>Use Edit details in the Users list to maintain contact, business and service details. Use the Access column to change job access.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>A note on attachments</h3>
          <ul className={styles.list}>
            <li>
              <strong>Adding and removing files</strong> counts as changing the job, so it needs
              full job access.
            </li>
            <li>
              <strong>Downloading</strong> is allowed on view only — those accounts can open every
              file on a job, but cannot add or delete any.
            </li>
            <li>
              Files are stored with the job they belong to, and every download link checks who is
              asking. Nothing is publicly accessible.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Worth knowing</h3>
          <ul className={styles.list}>
            <li>
              Creating an account does not send an email. Share the initial password securely;
              password resets remain available separately. Changing a contractor’s email or
              effective display name signs that person out, too.
            </li>
            <li>
              Changing someone&rsquo;s access, status or password <strong>signs them out on every
              device</strong>. They simply log back in and pick up their new access. You stay signed
              in yourself.
            </li>
            <li>
              A staff or contractor account set to &ldquo;no job access&rdquo; can still log in, but will not see
              any job data. That is expected, not a fault.
            </li>
            <li>
              If someone reports a &ldquo;Missing permissions&rdquo; message, the Access column on
              the Users screen is the first thing to check.
            </li>
          </ul>
        </section>
      </div>
    </Modal>
  );
}
